import React, { useEffect, useState, useCallback } from "react";
import { Card, Grid, Box, Typography, IconButton, CircularProgress, Button, Snackbar, Alert } from "@mui/material";
import whiteBackIcon from "../../../icons/whiteBackIcon.svg";
import { useStyles } from "../styles";
import { AppConfigSettingsViewEnum } from "../applicationsConfigSettings";
import { Editor } from 'react-draft-wysiwyg';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from "html-to-draftjs";
import { useDispatch, useSelector } from "react-redux";
import { ONE_MEGABYTE_SIZE } from "../../../utils/constants";
import debounce from 'lodash.debounce';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { getAllPropertyGroupsConfigs, getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState } from "../../../shared/sagas/propertyGroups/propertyGroupsSlice";

interface PropsType {
  setView: React.Dispatch<React.SetStateAction<AppConfigSettingsViewEnum>>;
}

const EditorToolbar = {
  options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'remove', 'history'],
  inline: {
    options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
  },
}

export default function ApplicationsLegalese({ setView }: PropsType) {
  const dispatch = useDispatch();
  const { selectedPropertyGroupAssignedConfigs, isUpdatingPropertyGroupConfigsSuccess, isUpdatingPropertyGroupConfigs } = useSelector(selectPropertyGroupDetailsState);

  const classes = useStyles();

  const getTypography = (text: string, fontWeight: number, fontSize: string, restProps?: object) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
        ...restProps,
      }}
    >
      {text}
    </Typography>
  )

  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
  useEffect(() => {
    if (selectedPropertyGroupAssignedConfigs.ApplicationsEditedLegaleseText) {
      let legaleseText = selectedPropertyGroupAssignedConfigs.ApplicationsEditedLegaleseText;
      if (selectedPropertyGroupAssignedConfigs.ApplicationsEditedLegaleseText[0] !== '<' && selectedPropertyGroupAssignedConfigs.ApplicationsEditedLegaleseText.slice(-1) !== '>') {
        legaleseText = `<p>${legaleseText}</p>`;
      }

      const blocksFromHTML = htmlToDraft(legaleseText);
      const content = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap,
      );

      setEditorState(EditorState.createWithContent(content));
    }
  }, [selectedPropertyGroupAssignedConfigs.ApplicationsEditedLegaleseText])

  const [pendingConfig, setPendingConfig] = React.useState<string|null>(null);

  useEffect(() => {
    const newText = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    setPendingConfig(newText);
  }, [editorState])

  const handleSave = () => {
    if(pendingConfig){
      setGlobalError("");
 
      const byteSize = new Blob([pendingConfig]).size;
      if (byteSize < ONE_MEGABYTE_SIZE) {
        dispatch(getUpdatePropertyGroupConfigs({ 
          propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
          productTypeId: 2,
          updatedPmConfig: {
            ApplicationsEditedLegaleseText: pendingConfig,
          }
        }));
      } else {
        setGlobalError("The size of your content is too large. Please reduce it and try again.");
        setSnackBarOpen(true);
      }
      setPendingConfig(null);
    }
  }
  
  const delayedConfigUpdate = useCallback(debounce(handleSave, 1000), [pendingConfig]);

  useEffect(() => {
    delayedConfigUpdate();
    return delayedConfigUpdate.cancel;
  }, [pendingConfig, delayedConfigUpdate]);

  const [isPublishing, setIsPublishing] = useState(false);
  const handlePublish = () => {
    setGlobalError("");
    const newText = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    const byteSize = new Blob([newText]).size;
    if (byteSize < ONE_MEGABYTE_SIZE) {
      setIsPublishing(true);
      dispatch(getUpdatePropertyGroupConfigs({ 
        propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
        productTypeId: 2,
        updatedPmConfig: {
          ApplicationsPublishedLegaleseText: newText,
        }
      }));
    } else {
      setGlobalError("The size of your content is too large. Please reduce it and try again.");
      setSnackBarOpen(true);
    }
  }

  useEffect(() => {
    if (isPublishing && !isUpdatingPropertyGroupConfigs && isUpdatingPropertyGroupConfigsSuccess) {
      setIsPublishing(false);
      dispatch(getAllPropertyGroupsConfigs());//TODO: why is this necessary?
    }
  }, [isUpdatingPropertyGroupConfigs, isUpdatingPropertyGroupConfigsSuccess, isPublishing])

  const [globalError, setGlobalError] = useState('');
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  
  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  const [tab, setTab] = React.useState('draft');

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {globalError}
        </Alert>
      </Snackbar>
      <Card sx={{ display: "flex", flexDirection: "column", color: "#000" }}>
        <Box className={classes.headerContainer} sx={{ backgroundColor: '#BE6531' }}>
          <IconButton
            sx={{ marginRight: "10px", marginTop: "-4px" }}
            onClick={() => setView(AppConfigSettingsViewEnum.Dashboard)}
          >
            <img src={whiteBackIcon} alt="" height="14px" />
          </IconButton>
          {getTypography('Application Legalese', 600, '24px', { color: '#fff' })}
        </Box>

        <Box sx={{ p: 2, pt: 0.5, pb: 1, position: 'relative', border: '3px #F1F0F5 solid', borderRadius: '24px', m: 2 }}>
          <Box sx={{ position: 'absolute', right: '16px', top: '14px' }}>
            <Button
              variant="contained"
              style={{
                textTransform: 'none',
                fontFamily: "'Open Sans', sans-serif",
                fontStyle: 'normal',
                fontWeight: 700,
                backgroundColor: '#39A2DB',
                borderRadius: '24px',
                width: '115px',
                height: '36.5px',
              }}
              onClick={handlePublish}
            >
              {isUpdatingPropertyGroupConfigs && isPublishing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : 'Publish'}
            </Button>
          </Box>

          <Box sx={{ mt: 1, mb: 1 }}>
            <TabContext value={tab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChangeTab} aria-label="content tab" sx={{ width: 'calc( 100% - 120px )' }}>
                  <Tab label="Draft" value="draft" sx={{ fontFamily: 'Work Sans', fontWeight: '700' }} />
                  <Tab label="Published" value="published" sx={{ fontFamily: 'Work Sans', fontWeight: '700' }} />
                </TabList>
              </Box>
              <TabPanel value="draft" sx={{ pt: 2, px: 2 }}>
                <Editor
                  editorState={editorState}
                  toolbarClassName={classes.toolbarClass}
                  wrapperClassName={classes.wrapperClass}
                  editorClassName={classes.editorClass}
                  toolbar={EditorToolbar}
                  onEditorStateChange={(currentState: EditorState) => setEditorState(currentState)}
                />
              </TabPanel>
              <TabPanel value="published">
                <div style={{ fontFamily: 'Work Sans' }} dangerouslySetInnerHTML={{ __html: (selectedPropertyGroupAssignedConfigs?.ApplicationsPublishedLegaleseText ?? '') }} />
              </TabPanel>
            </TabContext>
          </Box>
        </Box>
      </Card>
    </Grid>
  );
}
